import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Boxes, Tag, Users, Truck, Loader2, Settings2, Info, ArrowLeft } from 'lucide-react';
import { useCompany } from '@/context/CompanyContext';
import { usePermissions } from '@/hooks/usePermissions';
import { emitToast } from '@/components/ui/Toast';
import {
  DIRECTION_LABELS, ERP_PROVIDERS, FLOWS, STORE_PROVIDERS, defaultOptions,
  type FlowDef, type FlowDirection, type FlowOptionValues,
} from '@/lib/interfacesCatalog';
import { interfacesService, type InterfaceFlow } from '@/lib/interfacesService';
import { useInterfaceConnection } from './useInterfaceConnection';
import { FlowOptionsDrawer } from './FlowOptionsDrawer';

const FLOW_ICONS: Record<string, React.ReactNode> = {
  order_to_invoice: <FileText className="h-5 w-5" />,
  stock_to_store: <Boxes className="h-5 w-5" />,
  price_sync: <Tag className="h-5 w-5" />,
  customer_sync: <Users className="h-5 w-5" />,
  fulfillment_status: <Truck className="h-5 w-5" />,
};

export function InterfacesFlowsPage() {
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const { can } = usePermissions();
  const canEdit = can({ module: 'interfaces', level: 'editor' });
  const { connection, loading: connLoading, error: connError } = useInterfaceConnection(companyId);

  const [flows, setFlows] = useState<Record<string, InterfaceFlow>>({});
  const [flowsLoading, setFlowsLoading] = useState(false);
  const [flowsError, setFlowsError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<FlowDef | null>(null);

  const loadFlows = useCallback(async () => {
    if (!connection) return;
    setFlowsLoading(true);
    setFlowsError(null);
    try {
      const rows = await interfacesService.listFlows(connection.id);
      setFlows(Object.fromEntries(rows.map(r => [r.flow_key, r])));
    } catch (e) {
      console.error('Failed to load interface flows:', e);
      setFlowsError('לא הצלחנו לטעון את הממשקים');
    } finally {
      setFlowsLoading(false);
    }
  }, [connection]);

  useEffect(() => { loadFlows(); }, [loadFlows]);

  const persist = async (flow: FlowDef, patch: { is_enabled?: boolean; direction?: FlowDirection; options?: FlowOptionValues }) => {
    if (!connection || !companyId) return false;
    const existing = flows[flow.key];
    try {
      const saved = await interfacesService.saveFlow({
        company_id: companyId,
        connection_id: connection.id,
        flow_key: flow.key,
        is_enabled: patch.is_enabled ?? existing?.is_enabled ?? false,
        direction: patch.direction ?? existing?.direction ?? flow.allowedDirections[0],
        options: patch.options ?? { ...defaultOptions(flow), ...(existing?.options ?? {}) },
      });
      setFlows(prev => ({ ...prev, [flow.key]: saved }));
      return true;
    } catch (e) {
      console.error('Failed to save interface flow:', e);
      emitToast('השמירה נכשלה, נסו שוב', 'error');
      return false;
    }
  };

  const toggle = async (flow: FlowDef) => {
    setBusyKey(flow.key);
    const next = !flows[flow.key]?.is_enabled;
    if (await persist(flow, { is_enabled: next })) {
      emitToast(next ? `"${flow.title}" הופעל` : `"${flow.title}" כובה`, 'success');
    }
    setBusyKey(null);
  };

  const saveOptions = async (values: FlowOptionValues, direction: FlowDirection) => {
    if (!editing) return;
    if (await persist(editing, { options: values, direction })) {
      emitToast('ההגדרות נשמרו', 'success');
      setEditing(null);
    }
  };

  if (connLoading || flowsLoading) {
    return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sky-600" /></div>;
  }
  const loadError = connError ?? flowsError;
  if (loadError) {
    return <div className="mx-auto max-w-3xl rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">{loadError}</div>;
  }

  const ready = !!connection?.store_provider && !!connection?.erp_provider;
  const storeLabel = STORE_PROVIDERS.find(p => p.key === connection?.store_provider)?.label;
  const erpLabel = ERP_PROVIDERS.find(p => p.key === connection?.erp_provider)?.label;

  return (
    <div className="mx-auto max-w-5xl space-y-6" dir="rtl">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">ממשקים פעילים</h1>
        <p className="mt-1 text-gray-600">
          {ready ? `${storeLabel} ↔ ${erpLabel} — בחרו אילו ממשקים להפעיל ואיך כל אחד מהם יעבוד.` : 'בחרו אילו ממשקים להפעיל ואיך כל אחד מהם יעבוד.'}
        </p>
      </header>

      {!ready && (
        <Link
          to="/app/interfaces"
          className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-amber-800 transition-colors hover:bg-amber-100"
        >
          <span className="text-sm font-medium">כדי להפעיל ממשקים, בחרו קודם את האתר ואת מערכת הניהול</span>
          <ArrowLeft className="h-5 w-5" />
        </Link>
      )}

      <div className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50 px-5 py-4 text-sm text-sky-900">
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <p>ההגדרות נשמרות כבר עכשיו. ההעברה בפועל בין המערכות תתחיל לפעול בשלב הבא, לאחר בדיקת החיבור לרווחית.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {FLOWS.map(flow => {
          const saved = flows[flow.key];
          const enabled = !!saved?.is_enabled;
          const comingSoon = flow.status === 'coming_soon';
          const direction = saved?.direction ?? flow.allowedDirections[0];
          const toggleDisabled = comingSoon || !ready || !canEdit || busyKey === flow.key;

          return (
            <div
              key={flow.key}
              className={`flex flex-col rounded-2xl border bg-white p-5 shadow-sm transition-all duration-200 ${
                comingSoon ? 'border-dashed border-gray-200 opacity-70' : enabled ? 'border-sky-300 ring-1 ring-sky-100' : 'border-gray-200 hover:shadow-md'
              }`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className={`rounded-lg p-2 ${enabled ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-500'}`}>
                    {FLOW_ICONS[flow.key]}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{flow.title}</h3>
                    <p className="mt-0.5 text-sm text-gray-500">{flow.description}</p>
                  </div>
                </div>
                {comingSoon ? (
                  <span className="flex-shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">בקרוב</span>
                ) : (
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`הפעלת ${flow.title}`}
                    disabled={toggleDisabled}
                    onClick={() => toggle(flow)}
                    className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${enabled ? 'bg-sky-600' : 'bg-gray-300'} disabled:cursor-not-allowed disabled:opacity-50`}
                  >
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${enabled ? 'right-[22px]' : 'right-0.5'}`} />
                  </button>
                )}
              </div>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-4">
                <span className="text-xs font-medium text-gray-500">{DIRECTION_LABELS[direction]}</span>
                {!comingSoon && (
                  <button
                    type="button"
                    disabled={!ready}
                    onClick={() => setEditing(flow)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Settings2 className="h-4 w-4" /> {canEdit ? 'הגדרות' : 'צפייה בהגדרות'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && companyId && (
        <FlowOptionsDrawer
          companyId={companyId}
          flow={editing}
          initialValues={{ ...defaultOptions(editing), ...(flows[editing.key]?.options ?? {}) }}
          initialDirection={flows[editing.key]?.direction ?? editing.allowedDirections[0]}
          readOnly={!canEdit}
          onSave={saveOptions}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
